import React, { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X } from "lucide-react";
import { ABOUT_INTRO, MANIFESTO, ASSETS } from "@/lib/data";
import { Reveal } from "./Reveal";
import { SmokeReveal } from "./SmokeReveal";

const POSTERS = [
  { src: ASSETS.aboutPoster3, alt: "WPC 3.0 — The Rise of Pahlawan (Latest Event)" },
  { src: ASSETS.aboutPoster2, alt: "Wilayah Pahlawan Fight Card" },
  { src: ASSETS.aboutPoster, alt: "The Spirit of Pahlawan" },
];

export const About = () => {
  const [front, setFront] = useState(0);
  const [touched, setTouched] = useState(0); // manual tap resets the shuffle timer
  const [zoom, setZoom] = useState(false);

  useEffect(() => {
    if (zoom) return; // hold the shuffle while a poster is zoomed
    const t = setInterval(() => setFront((f) => (f + 1) % POSTERS.length), 5000);
    return () => clearInterval(t);
  }, [touched, zoom]);

  useEffect(() => {
    if (!zoom) return;
    const onKey = (e) => e.key === "Escape" && setZoom(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [zoom]);
  return (
    <section id="about" className="relative mx-auto max-w-7xl px-4 py-24 sm:px-8 lg:py-36">
      <SmokeReveal tint="pink" className="mb-16 max-w-3xl">
        <p className="font-mono-cap mb-4 text-[11px] text-neon-cyan">About Us / 00 — The Championship</p>
        <h2 className="font-display text-4xl leading-[0.95] sm:text-6xl">
          MORE THAN A <span className="gradient-text">CHAMPIONSHIP</span>
        </h2>
        <p className="mt-6 font-body text-lg text-slate-300">{ABOUT_INTRO}</p>
      </SmokeReveal>

      <div className="grid gap-10 lg:grid-cols-[1.15fr_0.85fr]">
        {/* Numbered manifesto chapters */}
        <div className="flex flex-col">
          {MANIFESTO.map((c, i) => (
            <Reveal
              key={c.index}
              delay={i * 0.05}
              className="group border-t border-white/10 py-8 transition-colors hover:border-neon-pink/50"
              data-testid={`manifesto-${c.index}`}
            >
              <div className="flex flex-col gap-4 sm:flex-row sm:gap-10">
                <div>
                  <h3 className="font-sub text-xl font-extrabold uppercase tracking-wide text-white sm:text-2xl">
                    {c.title}
                  </h3>
                  <p className="mt-3 max-w-xl font-body text-[15px] leading-relaxed text-slate-400">{c.body}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>

        {/* Stacked event posters — auto-shuffles slowly; tap a card to bring it to front */}
        <div className="relative hidden lg:block">
          <div className="sticky top-28">
            <div className="relative mx-auto aspect-[4/5] max-h-[34rem] w-full">
              {POSTERS.map((p, i) => {
                const depth = (i - front + POSTERS.length) % POSTERS.length; // 0 = front
                const isFront = depth === 0;
                return (
                  <motion.button
                    key={i}
                    onClick={() => {
                      if (isFront) setZoom(true);
                      else { setFront(i); setTouched((t) => t + 1); }
                    }}
                    initial={false}
                    animate={{
                      x: depth * 28,
                      y: depth * 24,
                      rotate: depth * 3,
                      scale: 1 - depth * 0.045,
                      zIndex: 20 - depth,
                      filter: `brightness(${1 - depth * 0.22})`,
                    }}
                    whileHover={isFront ? { scale: 1.02 } : { scale: 1 - depth * 0.045 + 0.02 }}
                    transition={{ type: "spring", stiffness: 120, damping: 22 }}
                    className="absolute inset-0 overflow-hidden rounded-2xl border border-white/10 glow-ring"
                    style={{ cursor: "pointer" }}
                    data-testid={`about-poster-${i}`}
                  >
                    {/* blurred self-fill so no plain black edges show beside full-size posters */}
                    <img src={p.src} alt="" aria-hidden className="absolute inset-0 h-full w-full scale-110 object-cover opacity-70 blur-xl" />
                    <img src={p.src} alt={p.alt} className="relative h-full w-full object-contain" />
                    {!isFront && <div className="absolute inset-0 bg-void/30" />}
                  </motion.button>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {zoom && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[90] flex items-center justify-center bg-void/90 p-4 backdrop-blur-md"
            onClick={() => setZoom(false)}
            data-testid="poster-lightbox"
          >
            <motion.img
              initial={{ scale: 0.92, y: 12 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.95, y: 8 }}
              transition={{ type: "spring", stiffness: 200, damping: 24 }}
              src={POSTERS[front].src}
              alt={POSTERS[front].alt}
              className="max-h-[88vh] max-w-full rounded-2xl border border-white/15 object-contain glow-ring"
            />
            <button
              onClick={() => setZoom(false)}
              className="absolute right-5 top-5 rounded-full bg-black/60 p-2 text-white transition-colors hover:bg-neon-pink"
              data-testid="poster-lightbox-close"
              aria-label="Close full-size poster"
            >
              <X className="h-5 w-5" />
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};
